import { reactive } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { getClientId } from "@utils/clientId";
import genToken from "@utils/generateToken";
import { createAnswerItem, createRangeItem, createCondition, getCurrentLocale } from "@stores/survey/cardPayloadHandler";
import { useSurveyStore } from "@stores/survey/survey";
import { useCardsStore } from "@stores/survey/cards";
import { useResultsV2Store } from "./resultsV2";
import { useResultsV2CardsStore } from "./resultsV2.cards";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useContactAttributesStore } from "@stores/survey/mailing/contactAttributes";
import { useConstantsStore } from "@stores/constants";
import { RECODE_KIND_DEFAULTS, productKind } from "@utils/recodeKinds";
import { computedCardOptions, cardTokenToOption, optionToCardToken } from "@utils/computeCardOptions";
import { computeOpenEndQuestionOptions } from "@utils/computeOpenEndQuestionOptions";
import { computedValuesOptions } from "@utils/computeConditionValuesOptions";
import { computedIntegerListOptions } from "@utils/computeAnswerOptions";
import { conditionCardToken } from "@utils/conditionData";
import { loopTokensByCardToken } from "@stores/survey/computeCardsList";
import { duplicateCategory, rangesByAnswer } from "@utils/recodeRanges";
import {
  allowsTypedValues,
  conditionTypeOptions,
  conditionValueInput,
  defaultConditionType,
  isComparisonType,
  resetConditionValues,
} from "@utils/conditionOperators";
import { useToastsStore } from "@/stores/toastsStore";
import useMyFetch from "@stores/useMyFetch";
import { capitalize } from "@utils/toolBox";
import { clearChartsCache } from "@utils/charts";
import { captureEvent } from "@/lib/analytics";

const CONDITION_ALLOWED_TYPES = [
  "q_single",
  "q_multiple",
  "q_ranking",
  "q_rating",
  "q_slider",
  "q_numeric",
  "q_city",
  "q_image_choice",
  "q_image_choice_multiple",
  "o_ab",
  "o_cells",
  "o_custom",
  "o_user_attributes",
  "q_open",
  "q_open_multiple",
  "q_matrix",
  "q_email",
  "q_image",
  "q_video",
  "q_audio",
  "q_zipcode",
  "q_nps",
  "o_quota",
  "q_hidden",
  "o_loop",
];

const OPEN_QUESTION_TYPES = ["q_open", "q_open_multiple"];

const SINGLE_MATCH_TYPES = ["q_single", "q_image_choice", "q_rating", "q_nps"];
const MULTIPLE_MATCH_TYPES = ["q_multiple", "q_ranking", "q_image_choice_multiple"];
const GROUP_SOURCE_TYPES = [...SINGLE_MATCH_TYPES, "q_multiple", "q_image_choice_multiple", "q_matrix", "o_user_attributes"];

function detectTransformationType(card) {
  const questionType = card.type;
  if (SINGLE_MATCH_TYPES.includes(questionType)) return "Single Match (Closed)";
  if (MULTIPLE_MATCH_TYPES.includes(questionType)) return "Multiple Match (Closed)";
  if (questionType === "q_matrix")
    return card.specs?.matrix_type === "multiple" ? "Multiple Match (Matrix)" : "Single Match (Matrix)";
  if (questionType === "o_user_attributes") return "User attribute";
  if (questionType === "q_numeric") return "Range";
  if (OPEN_QUESTION_TYPES.includes(questionType)) return "Single Match (Closed)";
  return null;
}

const V2_RECODE_KINDS = ["conditions", "open_end", "interview_codification", "url_param", "mailing", "geo", "group_answers"];

// Geo stock reads a zip code question or a numeric one (40% of the stock).
const GEO_SOURCE_TYPES = ["q_zipcode", "q_numeric"];

export const USER_ATTRIBUTE_SOURCE = "__user_attribute__";
const USER_ATTRIBUTE_OPTION_PREFIX = `${USER_ATTRIBUTE_SOURCE}:`;

function resolveRecodeKind(card) {
  const kind = productKind(card);
  return V2_RECODE_KINDS.includes(kind) ? kind : "conditions";
}

function isLegacyRangeRecode(specs) {
  return specs.recode_type === "question" && specs.transformation_type === "Range";
}

// Both backend engines use inclusive bounds and treat an empty min as 0,
// so a "between" condition on the input question is strictly equivalent.
function convertRangeToConditionsRanges(specs) {
  const inputCardToken = specs.input_question?.card_token || null;
  return (specs.ranges || []).map((range) => ({
    token: range.token || genToken(),
    to: range.to || range.value,
    conditions: [
      {
        ...createCondition(),
        card_token: inputCardToken,
        type: "between",
        min: range.min || "0",
        max: range.max || "0",
      },
    ],
  }));
}

export const useResultsV2RecodesStore = defineStore("resultsV2Recodes", () => {
  const toast = useToastsStore();
  const surveyStore = useSurveyStore();
  const { token: surveyToken } = storeToRefs(surveyStore);
  const cardsStore = useCardsStore();

  const recodeConfigs = reactive({});
  const loadingRecodes = reactive({});
  const recodeErrors = reactive({});

  function sourceCandidates(cardToken) {
    const config = recodeConfigs[cardToken];
    const all = cardsStore.cards.filter((c) => c.specs?.is_pii !== true);
    if (config?.surface !== "programming") return all;
    const index = cardsStore.findIndex(cardToken);
    return index === -1 ? all : all.filter((c) => cardsStore.findIndex(c.token) < index);
  }

  // The dashboard has no locale switcher, so its form only writes the analysis locale. Give the default
  // locale that value when it is blank: the API backfills the other locales from the default one, and
  // a blank default would be persisted as-is. Existing translations are left untouched.
  function withDefaultLocale(value, config) {
    if (config.surface !== "dashboard") return value;
    const { defaultLocale, currentLocale } = storeToRefs(useSurveyLocalesStore());
    const defaultToken = defaultLocale.value?.token;
    const text = value?.[currentLocale.value?.token];
    if (!defaultToken || value?.[defaultToken]?.trim() || !text?.trim()) return value;
    return { ...value, [defaultToken]: text };
  }

  function specsFromConfig(config) {
    const answers = config.answers.map((a) => ({ code: a.code, title: withDefaultLocale(a.title, config), token: a.token }));
    const ranges = config.ranges.map((r, i) => ({ ...r, to: answers[i]?.code || r.to }));
    const specs = {
      recode_type: config.recodeType,
      answers,
      ranges,
      input_question: config.inputQuestion,
      transformation_type: config.transformationType,
      contact_attribute: config.contactAttribute,
      url_parameter: config.urlParameter || null,
    };
    if (config.recodeKind === "open_end" || config.recodeKind === "interview_codification") {
      specs.ai_recode_instruction = config.aiInstruction;
    }
    if (config.recodeKind === "group_answers") specs.attribute_name = config.attributeName;
    return specs;
  }

  // --- Init ---

  function initNewRecode(cardToken, recodeKind = "conditions", { surface = "dashboard" } = {}) {
    const defaults = RECODE_KIND_DEFAULTS[recodeKind] || RECODE_KIND_DEFAULTS.conditions;
    recodeErrors[cardToken] = [];
    recodeConfigs[cardToken] = {
      cardToken,
      recodeKind,
      surface,
      name: "",
      question: {},
      recodeType: defaults.recodeType,
      inputQuestion: null,
      transformationType: defaults.transformationType,
      answers: [],
      ranges: [],
      contactAttribute: null,
      urlParameter: "",
      zoning: null,
      attributeName: null,
      aiInstruction: "",
      isGeneratingCategories: false,
      groupValues: false,
      isNew: true,
    };

    if (recodeKind === "conditions" || recodeKind === "group_answers") {
      addAnswer(cardToken);
    }
  }

  function duplicateRecode(sourceToken, newToken) {
    if (!recodeConfigs[sourceToken]) initExistingRecode(sourceToken);
    const source = recodeConfigs[sourceToken];
    if (!source) return null;

    const clone = JSON.parse(JSON.stringify(source));
    clone.answers = (clone.answers || []).map((a) => ({ ...a, token: genToken() }));
    clone.ranges = (clone.ranges || []).map((r) => ({ ...r, token: genToken() }));
    clone.cardToken = newToken;
    clone.name = source.name ? `${source.name} (copy)` : "";
    clone.anchorToken = sourceToken;
    clone.isGeneratingCategories = false;
    clone.isNew = true;

    recodeConfigs[newToken] = clone;
    return clone.recodeKind;
  }

  function initExistingRecode(cardToken, { surface = "dashboard" } = {}) {
    const card = cardsStore.findByToken(cardToken);
    if (!card) return;

    const specs = card.specs || {};
    const recodeKind = resolveRecodeKind(card);
    const legacyRange = isLegacyRangeRecode(specs);
    recodeErrors[cardToken] = [];

    const stockRanges = legacyRange ? convertRangeToConditionsRanges(specs) : specs.ranges;
    const ranges = rangesByAnswer(specs.answers, stockRanges, recodeKind);
    const answers = (specs.answers || []).map((a, i) => ({
      title: a.title || {},
      from: ranges[i]?.from || [],
      token: a.token,
      code: a.code,
    }));

    recodeConfigs[cardToken] = {
      cardToken,
      recodeKind,
      surface,
      name: card.name || "",
      question: specs.question || {},
      recodeType: legacyRange ? "conditions" : specs.recode_type || "question",
      inputQuestion: legacyRange ? null : specs.input_question || userAttributeInputQuestion(specs),
      transformationType: legacyRange ? "Conditions (Single)" : specs.transformation_type || null,
      answers,
      ranges,
      contactAttribute: specs.contact_attribute || null,
      urlParameter: specs.url_parameter || "",
      zoning: recodeKind === "geo" ? zoningForTransformationType(specs.transformation_type) : null,
      attributeName: specs.attribute_name || null,
      aiInstruction: specs.ai_recode_instruction || "",
      isGeneratingCategories: false,
      groupValues: recodeKind === "mailing" && specs.recode_type === "contact_attribute" && answers.length > 0,
      isNew: false,
    };
  }

  function zoningForTransformationType(transformationType) {
    const { recodeZonings } = useConstantsStore();
    return recodeZonings.find((z) => z.transformation_type === transformationType)?.key || null;
  }

  function getZoningOptions() {
    const { recodeZonings } = useConstantsStore();
    return recodeZonings.map((z) => ({ label: z.label, value: z.key }));
  }

  function getZoningCategories(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config?.zoning) return [];
    const { recodeZonings } = useConstantsStore();
    return recodeZonings.find((z) => z.key === config.zoning)?.categories || [];
  }

  function getGeoSourceOptions(cardToken) {
    const cards = sourceCandidates(cardToken).filter((c) => GEO_SOURCE_TYPES.includes(c.type));
    return computedCardOptions({ cards });
  }

  function updateZoning(cardToken, zoningKey) {
    const config = recodeConfigs[cardToken];
    if (!config) return;
    const { recodeZonings } = useConstantsStore();
    const zoning = recodeZonings.find((z) => z.key === zoningKey);
    config.zoning = zoning?.key || null;
    config.transformationType = zoning?.transformation_type || null;
    config.answers = [];
    config.ranges = [];
  }

  // --- Updates ---

  function updateInputQuestion(cardToken, value) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    config.inputQuestion = value;
    config.answers = [];
    config.ranges = [];

    if (config.recodeKind === "geo") return;

    const sourceCard = value?.card_token ? cardsStore.findByToken(value.card_token) : null;
    if (!sourceCard) return;

    config.transformationType = detectTransformationType(sourceCard);
    config.attributeName = sourceCard.type === "o_user_attributes" ? sourceCard.specs?.attribute_name || null : null;
    addAnswer(cardToken);
  }

  // --- Group answers ---

  function userAttributeInputQuestion(specs) {
    if (!specs.attribute_name) return null;
    const card = cardsStore.cards.find((c) => c.type === "o_user_attributes" && c.specs?.attribute_name === specs.attribute_name);
    return card ? { card_token: card.token, item_token: null } : null;
  }

  function getGroupSourceOptions(cardToken) {
    const cards = sourceCandidates(cardToken).filter((c) => GROUP_SOURCE_TYPES.includes(c.type));
    return computedCardOptions({ cards, handleMatrix: false, handleOUserAttributes: false });
  }

  function groupSourceCard(cardToken) {
    const token = recodeConfigs[cardToken]?.inputQuestion?.card_token;
    return token ? cardsStore.findByToken(token) : null;
  }

  function isGroupSourceMatrix(cardToken) {
    return groupSourceCard(cardToken)?.type === "q_matrix";
  }

  function isGroupSourceMultiple(cardToken) {
    const source = groupSourceCard(cardToken);
    if (!source) return false;
    return MULTIPLE_MATCH_TYPES.includes(source.type) || (source.type === "q_matrix" && source.specs?.matrix_type === "multiple");
  }

  function isGroupSourceUserAttribute(cardToken) {
    return groupSourceCard(cardToken)?.type === "o_user_attributes";
  }

  function groupSourceItemCount(cardToken) {
    return groupSourceCard(cardToken)?.specs?.items?.length || 0;
  }

  // The source's answers, keyed the way the Match strategies read them: codes for
  // questions (rating / NPS points included), declared value tokens for a user attribute.
  function getGroupAnswerOptions(cardToken) {
    const source = groupSourceCard(cardToken);
    if (!source) return [];
    const { localizeText } = useSurveyLocalesStore();
    if (source.type === "o_user_attributes") {
      return (source.specs?.attribute_values || []).map((v) => ({ label: localizeText(v.title) || v.code, value: v.token }));
    }
    if (source.type === "q_rating" || (source.type === "q_matrix" && source.specs?.matrix_type === "rating")) {
      const { min, max } = source.specs?.rating_limits || {};
      return computedIntegerListOptions(parseInt(min, 10), parseInt(max, 10));
    }
    if (source.type === "q_nps" || (source.type === "q_matrix" && source.specs?.matrix_type === "nps")) {
      return computedIntegerListOptions(0, 10);
    }
    return (source.specs?.answers || []).map((a) => ({ label: `${a.code} · ${localizeText(a.title) || ""}`.trim(), value: a.code }));
  }

  // An answer belongs to one category at most: the ones taken elsewhere are disabled.
  function groupAnswersExclusive(cardToken) {
    return !isGroupSourceMultiple(cardToken);
  }

  function getGroupAnswerOptionsFor(cardToken, categoryIndex) {
    if (!groupAnswersExclusive(cardToken)) return getGroupAnswerOptions(cardToken);
    const config = recodeConfigs[cardToken];
    const used = new Set((config?.answers || []).flatMap((a, i) => (i === categoryIndex ? [] : a.from || [])));
    return getGroupAnswerOptions(cardToken).map((o) => ({ ...o, disabled: used.has(o.value) }));
  }

  function addAnswer(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    const answer = createAnswerItem({ length: config.answers.length });
    config.answers.push({
      title: answer.title,
      from: [],
      token: answer.token,
      code: answer.code,
    });

    const rangeTypeMap = {
      interview_codification: "ai_chat_recode",
      open_end: "open_ai_recode",
      conditions: "conditions",
      mailing: "closed",
      url_param: "closed",
    };
    const rangeType = rangeTypeMap[config.recodeKind] || "closed";
    const range = createRangeItem(rangeType);
    range.to = answer.code;
    if (rangeType === "conditions") range.conditions = [seededCondition(config)];
    config.ranges.push(range);
  }

  // The answer code encodes its position (1, 2, 3…); range.to must stay in sync.
  function renumberAnswers(config) {
    config.answers.forEach((answer, i) => {
      answer.code = (i + 1).toString();
      if (config.ranges[i]) config.ranges[i].to = answer.code;
    });
  }

  function removeAnswer(cardToken, index) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    config.answers.splice(index, 1);
    config.ranges.splice(index, 1);
    renumberAnswers(config);
  }

  function duplicateAnswer(cardToken, index) {
    const config = recodeConfigs[cardToken];
    if (!config?.answers[index] || !config.ranges[index]) return;

    const { answer, range } = duplicateCategory(config.answers[index], config.ranges[index], config.answers);
    config.answers.splice(index + 1, 0, answer);
    config.ranges.splice(index + 1, 0, range);
    renumberAnswers(config);
  }

  function updateAnswerTitle(cardToken, index, locale, title) {
    const config = recodeConfigs[cardToken];
    if (!config?.answers[index]) return;
    config.answers[index].title[locale] = title;
  }

  function bulkSetAnswers(cardToken, titles, locale) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    // Index existing answers by title: each title maps to a queue of reusable
    // {answer, range} pairs (the queue handles duplicate titles). Reusing the
    // objects keeps their token and configured conditions across a bulk edit.
    const reusableByTitle = new Map();
    config.answers.forEach((answer, i) => {
      const key = (answer.title?.[locale] || "").trim();
      if (!reusableByTitle.has(key)) reusableByTitle.set(key, []);
      reusableByTitle.get(key).push({ answer, range: config.ranges[i] });
    });

    config.answers = [];
    config.ranges = [];
    titles.forEach((title) => {
      const reused = reusableByTitle.get(title)?.shift();
      if (reused?.range) {
        config.answers.push(reused.answer);
        config.ranges.push(reused.range);
      } else {
        addAnswer(cardToken);
        if (locale) updateAnswerTitle(cardToken, config.answers.length - 1, locale, title);
      }
    });

    renumberAnswers(config);
  }

  function updateAnswerMatchings(cardToken, index, matchings) {
    const config = recodeConfigs[cardToken];
    if (!config) return;
    config.answers[index].from = matchings;
    if (config.ranges[index]) config.ranges[index].from = matchings;
  }

  // --- Conditions ---

  const conditionOperatorOptions = [
    { label: "Or", value: "or" },
    { label: "And", value: "and" },
  ];

  function conditionSourceCard(questionCardToken) {
    if (!questionCardToken || questionCardToken === USER_ATTRIBUTE_SOURCE) return null;
    return cardsStore.findByToken(conditionCardToken(questionCardToken)) || null;
  }

  function getConditionTypeOptions(questionCardToken, currentType = null) {
    return conditionTypeOptions(conditionSourceCard(questionCardToken), currentType);
  }

  function getConditionValueInput(questionCardToken) {
    return conditionValueInput(conditionSourceCard(questionCardToken));
  }

  // A raw respondent attribute declares no values: they are typed.
  function allowsTypedConditionValues(questionCardToken) {
    if (questionCardToken === USER_ATTRIBUTE_SOURCE) return true;
    return allowsTypedValues(conditionSourceCard(questionCardToken));
  }

  function updateConditionMode(cardToken, mode) {
    const config = recodeConfigs[cardToken];
    if (!config) return;
    config.transformationType = mode === "multiple" ? "Conditions (Multiple)" : "Conditions (Single)";
  }

  function lastUsedCondition(config) {
    for (let i = config.ranges.length - 1; i >= 0; i--) {
      const conditions = config.ranges[i]?.conditions || [];
      for (let j = conditions.length - 1; j >= 0; j--) {
        if (conditionCardToken(conditions[j].card_token)) return conditions[j];
      }
    }
    return null;
  }

  function seededCondition(config) {
    const condition = createCondition();
    const last = lastUsedCondition(config);
    if (last) condition.card_token = typeof last.card_token === "object" ? { ...last.card_token } : last.card_token;
    if (last?.attribute_name) condition.attribute_name = last.attribute_name;
    return condition;
  }

  function addCondition(cardToken, answerIndex) {
    const config = recodeConfigs[cardToken];
    if (!config?.ranges[answerIndex]) return;
    config.ranges[answerIndex].conditions.push(seededCondition(config));
  }

  function conditionItemOptions(questionCardToken) {
    const card = conditionSourceCard(questionCardToken);
    if (card?.type !== "q_matrix") return [];
    const { localizeText } = useSurveyLocalesStore();
    return (card.specs?.items || []).map((item, i) => ({ label: localizeText(item.title) || `Item ${i + 1}`, value: item.token }));
  }

  function conditionItemToken(condition) {
    return typeof condition.card_token === "object" ? condition.card_token?.item_token || null : null;
  }

  function conditionNeedsItem(condition) {
    return conditionItemOptions(condition.card_token).length > 0 && !conditionItemToken(condition);
  }

  function removeCondition(cardToken, answerIndex, conditionIndex) {
    const config = recodeConfigs[cardToken];
    if (!config?.ranges[answerIndex]?.conditions) return;
    config.ranges[answerIndex].conditions.splice(conditionIndex, 1);
  }

  function updateConditionField(cardToken, answerIndex, conditionIndex, field, value) {
    const config = recodeConfigs[cardToken];
    const condition = config?.ranges[answerIndex]?.conditions?.[conditionIndex];
    if (!condition) return;

    if (field === "card_token") {
      resetConditionValues(condition);
      delete condition.attribute_name;
      if (typeof value === "string" && value.startsWith(USER_ATTRIBUTE_OPTION_PREFIX)) {
        condition.card_token = USER_ATTRIBUTE_SOURCE;
        condition.attribute_name = value.slice(USER_ATTRIBUTE_OPTION_PREFIX.length);
        condition.type = "is";
      } else {
        const parsed = optionToCardToken(value);
        const token = conditionCardToken(parsed);
        condition.card_token = conditionSourceCard(token)?.type === "q_matrix" ? { card_token: token, item_token: null } : parsed;
        condition.type = defaultConditionType(conditionSourceCard(token));
      }
    } else if (field === "item_token") {
      condition.card_token = { card_token: conditionCardToken(condition.card_token), item_token: value };
    } else if (field === "type") {
      if (isComparisonType(value) !== isComparisonType(condition.type)) resetConditionValues(condition);
      condition.type = value;
    } else {
      condition[field] = value;
    }
  }

  function updateConditionNumericValue(cardToken, answerIndex, conditionIndex, field, value) {
    const config = recodeConfigs[cardToken];
    const condition = config?.ranges[answerIndex]?.conditions?.[conditionIndex];
    if (!condition) return;
    condition[field] = value;
  }

  // The source dropdown of a condition, grouped by nature. "UA. name" reads a respondent attribute with no card.
  const CONDITION_SOURCE_GROUPS = [
    { label: "Questions", match: (c) => !["o_custom", "o_user_attributes"].includes(c.type) },
    { label: "User attributes", match: (c) => c.type === "o_user_attributes", handlers: { handleOUserAttributes: true }, extra: userAttributeOptions },
    { label: "Custom cards", match: (c) => c.type === "o_custom", handlers: { handleCustom: true } },
  ];

  // An o_user_attributes card already offers its attribute with the declared values: only the names no card declares are raw sources.
  function userAttributeOptions(cards) {
    const declared = new Set(cards.filter((c) => c.type === "o_user_attributes").map((c) => c.specs?.attribute_name));
    const names = new Set(cards.map((c) => c.specs?.attribute_name).filter((name) => name && !declared.has(name)));
    return [...names].map((name) => ({ label: `UA. ${name}`, value: `${USER_ATTRIBUTE_OPTION_PREFIX}${name}` }));
  }

  function getConditionQuestionOptions(cardToken) {
    const cards = sourceCandidates(cardToken).filter((c) => CONDITION_ALLOWED_TYPES.includes(c.type));
    const options = CONDITION_SOURCE_GROUPS.flatMap(({ label, match, handlers = {}, extra }) => {
      const items = computedCardOptions({ cards: cards.filter(match), handleMatrix: false, handleLoop: true, hideChartsRecodes: false, ...handlers });
      if (extra) items.push(...extra(cards));
      return items.length ? [{ label, value: `__group_${label}__`, disabled: true }, ...items] : [];
    });
    return [...options, ...referencedSourceOptions(cardToken, options)];
  }

  // Stock conditions may read a card placed after the recode, or a respondent attribute no card
  // declares (converted user attribute recodes): not offered, but they keep their name.
  function referencedSourceOptions(cardToken, options) {
    const known = new Set(options.map((o) => o.value));
    const conditions = (recodeConfigs[cardToken]?.ranges || []).flatMap((r) => r.conditions || []);
    return conditions.flatMap((condition) => {
      const value = conditionSourceOption(condition);
      if (!value || known.has(value)) return [];
      known.add(value);
      if (condition.card_token === USER_ATTRIBUTE_SOURCE) return [{ label: `UA. ${condition.attribute_name}`, value }];
      const card = cardsStore.findByToken(conditionCardToken(condition.card_token));
      return card ? [{ label: card.name || "(untitled)", value, name: card.name }] : [];
    });
  }

  function conditionSourceOption(condition) {
    if (condition.card_token === USER_ATTRIBUTE_SOURCE) return `${USER_ATTRIBUTE_OPTION_PREFIX}${condition.attribute_name || ""}`;
    const raw = condition.card_token;
    if (typeof raw !== "object" || raw === null) return cardTokenToOption(raw);
    if (raw.item_token) return cardTokenToOption(raw.card_token);
    return cardTokenToOption(raw, "user_attribute" in raw);
  }

  function getConditionValuesOptions(cardTokenValue) {
    if (!cardTokenValue || cardTokenValue === USER_ATTRIBUTE_SOURCE) return [];
    const token = conditionCardToken(cardTokenValue);
    const card = cardsStore.findByToken(token);
    if (!card) return [];
    // A custom card's values derive from the question it reads (set_n_answers counts its answers, answers_sample picks them).
    const sourceCard = card.type === "o_custom" ? cardsStore.findByToken(card.specs?.question_reference) : undefined;
    return computedValuesOptions(card, sourceCard) || [];
  }

  // --- Open End question options ---

  function getOpenEndQuestionOptions(cardToken) {
    const { localizeText } = useSurveyLocalesStore();
    return computeOpenEndQuestionOptions(sourceCandidates(cardToken), localizeText);
  }

  // --- AI Category Generation ---

  function setAiInstruction(cardToken, instruction) {
    const config = recodeConfigs[cardToken];
    if (!config) return;
    config.aiInstruction = instruction;
  }

  async function generateCategories(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    config.isGeneratingCategories = true;

    const isAiChat = config.recodeKind === "interview_codification";
    const cardTokenSource = config.inputQuestion?.card_token || null;
    const title = config.question ? Object.values(config.question).find((v) => v) || "" : "";

    const { response } = await useMyFetch(`/surveys/${surveyToken.value}/cards/create_categories`)
      .post({
        card_token: cardToken,
        context: config.aiInstruction,
        card_token_source: cardTokenSource,
        is_ai_chat: isAiChat,
        title,
      })
      .json();

    if (response.value.ok) captureEvent("recode_categories_generated", { card_token: cardToken });
  }

  function handleAiCategoriesReceived(message) {
    if (message?.type !== "ai_recode_categories" || !message?.body?.response) return;

    const { categories, card_token: cardToken, is_ai_chat: isAiChat } = message.body.response;
    const config = recodeConfigs[cardToken];
    if (!config) return;

    const { currentLocale } = storeToRefs(useSurveyLocalesStore());
    const locale = currentLocale.value?.token;

    config.ranges = [];
    config.answers = [];

    const rangeType = isAiChat ? "ai_chat_recode" : "open_ai_recode";
    categories.forEach((category, index) => {
      const answer = createAnswerItem({ length: index });
      answer.title[locale] = category;
      config.answers.push({
        title: answer.title,
        from: rangeType,
        token: answer.token,
        code: answer.code,
      });
      const range = createRangeItem(rangeType);
      range.to = answer.code;
      config.ranges.push(range);
    });

    config.isGeneratingCategories = false;
  }

  // --- Contact Attributes ---

  function getContactAttributeOptions() {
    const contactAttributesStore = useContactAttributesStore();
    const openOpts = contactAttributesStore.getOpenContactAttributeOptions || [];
    const singleOpts = contactAttributesStore.getSingleContactAttributeOptions || [];
    return [...openOpts, ...singleOpts];
  }

  function updateContactAttribute(cardToken, attributeName) {
    const config = recodeConfigs[cardToken];
    if (!config || config.contactAttribute === attributeName) return; // re-picking the same attribute keeps the categories

    config.contactAttribute = attributeName;
    config.answers = [];
    config.ranges = [];

    const contactAttributesStore = useContactAttributesStore();
    const isOpen = contactAttributesStore.getOpenContactAttributeOptions.some(
      (o) => o.value === attributeName,
    );

    if (isOpen) {
      config.transformationType = "Mailing contact attribute (Open)";
    } else {
      config.transformationType = "Mailing contact attribute";
    }
  }

  function setContactAttributeValueAnswers(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config) return;

    config.answers = [];
    config.ranges = [];
    getContactAttributeValueOptions(cardToken).forEach(({ value }) => {
      addAnswer(cardToken);
      updateAnswerMatchings(cardToken, config.answers.length - 1, [value]);
      config.answers[config.answers.length - 1].title = { [getCurrentLocale()]: value };
    });
  }

  function getContactAttributeValueOptions(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config?.contactAttribute) return [];

    const contactAttributesStore = useContactAttributesStore();
    const values = contactAttributesStore.contactAttributes?.single?.[config.contactAttribute];
    if (!values) return [];
    return values.map((v) => ({ label: v, value: v }));
  }

  function isContactAttributeOpen(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config?.contactAttribute) return false;
    const contactAttributesStore = useContactAttributesStore();
    return contactAttributesStore.getOpenContactAttributeOptions.some(
      (o) => o.value === config.contactAttribute,
    );
  }

  function isContactAttributeSingle(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config?.contactAttribute) return false;
    const contactAttributesStore = useContactAttributesStore();
    return contactAttributesStore.getSingleContactAttributeOptions.some(
      (o) => o.value === config.contactAttribute,
    );
  }

  // --- Save & Compute ---

  function recodeSourceQuestionToken(config) {
    if (config.inputQuestion?.card_token) return config.inputQuestion.card_token;
    if (config.recodeKind === "conditions") {
      const firstConditionToken = config.ranges?.[0]?.conditions?.[0]?.card_token;
      if (firstConditionToken) return conditionCardToken(firstConditionToken);
    }
    return null;
  }

  function loopSafeAnchor(token, sourceQuestionToken) {
    const loopTokens = loopTokensByCardToken(cardsStore.cards);
    const loopToken = loopTokens.get(token) ?? null;
    const sourceLoopToken = loopTokens.get(sourceQuestionToken) ?? null;
    if (loopToken === sourceLoopToken) return token;
    if (sourceLoopToken) return null;
    const startIndex = cardsStore.cards.findIndex((c) => c.token === loopToken);
    const loopEnd = cardsStore.cards
      .slice(startIndex + 1)
      .find((c) => c.type === "o_loop" && c.specs?.position === "end");
    return loopEnd?.token || token;
  }

  function precedingDashboardCardToken(cardToken, sourceQuestionToken) {
    const resultsStore = useResultsV2Store();
    const viewCards = [...useResultsV2CardsStore().cards].sort((a, b) => a.position - b.position);
    const index = viewCards.findIndex((c) => c._recodeToken === cardToken);
    if (index === -1) return null;
    const preceding = viewCards
      .slice(0, index)
      .reverse()
      .find((c) => ["question", "recode"].includes(c.card_type) && c.card_id != null);
    if (!preceding) return null;
    const token = resultsStore.recodeTokenForViewCard(preceding);
    return token && cardsStore.findByToken(token) ? loopSafeAnchor(token, sourceQuestionToken) : null;
  }

  function setRecodeErrors(cardToken, errorValue) {
    const { labelizeLocale } = useSurveyLocalesStore();
    const list = Array.isArray(errorValue) ? errorValue : [];
    recodeErrors[cardToken] = list.map((e) => capitalize(labelizeLocale(typeof e === "string" ? e : e?.message)));
  }

  async function saveAndCompute(cardToken) {
    const config = recodeConfigs[cardToken];
    if (!config) return false;

    recodeErrors[cardToken] = [];
    loadingRecodes[cardToken] = true;
    const clientId = getClientId();
    // Interviews surface shows progress via the global toaster (with_progress), not an in-card loader.
    const withProgress = config.surface === "interviews";

    if (config.recodeKind === "mailing" && isContactAttributeSingle(cardToken) && !config.groupValues) {
      setContactAttributeValueAnswers(cardToken);
    }

    const specs = { ...specsFromConfig(config), question: withDefaultLocale(config.question, config) };

    if (config.isNew) {
      const duplicationAnchor =
        config.anchorToken && cardsStore.findByToken(config.anchorToken) ? config.anchorToken : null;
      const sourceQuestionToken = recodeSourceQuestionToken(config);
      const anchorToken =
        duplicationAnchor ||
        precedingDashboardCardToken(cardToken, sourceQuestionToken) ||
        sourceQuestionToken ||
        cardsStore.cards.filter((c) => c.token !== cardToken && c.token).at(-1)?.token ||
        null;

      cardsStore.addCard(
        { token: cardToken, name: config.name, type: "q_hidden", is_for_analytics: true, is_charts: true, is_admin: false, specs },
        anchorToken,
      );
    } else {
      const card = cardsStore.findByToken(cardToken);
      if (card) {
        card.name = config.name;
        card.specs = { ...card.specs, ...specs };
        cardsStore.updateCard(cardToken);
      }
    }

    const { ok, card: savedCard, errors } = await cardsStore.saveCard(cardToken, { withProgress, clientId });
    if (!ok) {
      if (config.isNew) cardsStore.removeCard(cardToken);
      loadingRecodes[cardToken] = false;
      setRecodeErrors(cardToken, errors);
      return false;
    }

    clearChartsCache();

    if (config.surface !== "interviews" && savedCard?.id != null) {
      const viewCard = useResultsV2CardsStore().cards.find((c) => c._recodeToken === cardToken);
      if (viewCard && viewCard.card_id == null) viewCard.card_id = savedCard.id;
    }

    return true;
  }

  // DELETE the recode card; returns true on success (or 404), toasts + false otherwise.
  async function deleteRecodeCard(cardToken) {
    const { error, statusCode } = await useMyFetch(`/surveys/${surveyToken.value}/cards/${cardToken}`)
      .delete()
      .json();
    const status = statusCode.value;
    if ((status >= 200 && status < 300) || status === 404) {
      await cardsStore.fetch();
      await useResultsV2Store().refreshChartsData({ bypassCache: true });
      if (status !== 404) captureEvent("recode_card_deleted", { card_token: cardToken });
      return true;
    }
    toast.error("Failed to delete recode", typeof error.value === "string" ? error.value : null);
    return false;
  }

  async function deleteRecode(cardToken) {
    if (!cardToken) {
      toast.error("Failed to delete recode", "Missing recode token");
      return false;
    }

    // Interviews surface has no view cards to clean up; just delete the recode card.
    // The caller (Interviews page) refreshes the table afterwards.
    if (recodeConfigs[cardToken]?.surface === "interviews") {
      const ok = await deleteRecodeCard(cardToken);
      if (ok) cleanup(cardToken);
      return ok;
    }

    // Capture which view cards reference this recode BEFORE the API call —
    const resultsStore = useResultsV2Store();
    const cardsStore = useResultsV2CardsStore();
    const orphanedCardIds = cardsStore.cards
      .filter((c) => resultsStore.recodeTokenForViewCard(c) === cardToken)
      .map((c) => c.id);

    if (!(await deleteRecodeCard(cardToken))) return false;

    // Remove the orphaned view cards BEFORE the refetch so they never render
    // their "Recode not found" empty state.
    orphanedCardIds.forEach((id) => cardsStore.removeCard(id));

    await useSurveyStore().fetch(surveyToken.value);
    await resultsStore.fetchAvailableCards();

    cleanup(cardToken);
    return true;
  }

  function stopLoading(cardToken) {
    loadingRecodes[cardToken] = false;
  }

  function cleanup(cardToken) {
    delete recodeConfigs[cardToken];
    delete loadingRecodes[cardToken];
    delete recodeErrors[cardToken];
  }

  return {
    recodeConfigs,
    loadingRecodes,
    recodeErrors,

    initNewRecode,
    initExistingRecode,
    duplicateRecode,
    updateInputQuestion,
    addAnswer,
    removeAnswer,
    duplicateAnswer,
    updateAnswerTitle,
    updateAnswerMatchings,
    bulkSetAnswers,

    // Conditions
    conditionOperatorOptions,
    getConditionTypeOptions,
    getConditionValueInput,
    allowsTypedConditionValues,
    updateConditionMode,
    addCondition,
    removeCondition,
    conditionSourceOption,
    conditionItemOptions,
    conditionItemToken,
    conditionNeedsItem,
    updateConditionField,
    updateConditionNumericValue,
    getConditionQuestionOptions,
    getConditionValuesOptions,

    // Open end
    getOpenEndQuestionOptions,

    getZoningOptions,
    getZoningCategories,
    getGeoSourceOptions,
    updateZoning,

    // Group answers
    getGroupSourceOptions,
    isGroupSourceMatrix,
    isGroupSourceMultiple,
    isGroupSourceUserAttribute,
    groupAnswersExclusive,
    groupSourceItemCount,
    getGroupAnswerOptionsFor,

    // AI generation
    setAiInstruction,
    generateCategories,
    handleAiCategoriesReceived,

    // Contact attributes
    getContactAttributeOptions,
    updateContactAttribute,
    getContactAttributeValueOptions,
    isContactAttributeOpen,
    isContactAttributeSingle,

    specsFromConfig,

    // Save/delete
    saveAndCompute,
    deleteRecode,
    stopLoading,
    cleanup,
  };
});
